// infrastructure/api/serviceCodeApi.js
import { auth } from 'boot/firebase';
import { serverTimestamp } from 'firebase/firestore';
import ServiceCodesDB from 'src/fb/serviceCodes-db';
import TreatmentServicesDB from 'src/fb/treatmentServices-db';

import { ServiceCodeAlreadyExists } from '../../application/ports/serviceCodeErrors';
import { SERVICE_CODE_ORIGIN } from '../../domain/constants/serviceCode';
import { customCodeDocumentId, toDocument, toServiceCodes } from '../mappers/serviceCodeMapper';

/**
 * Reads and writes for service codes — the managed catalog, and a clinic's own.
 *
 * ONE COLLECTION, TWO POPULATIONS. `serviceCodes` holds the global catalog (no `clinic` field, one
 * shared set every clinic sees identically) alongside each clinic's org-authored codes (scoped by a
 * bare `clinic`). They live together because they are the same entity: a charge line, a treatment
 * service's default and every resolver treat the two alike, and splitting the collection would force
 * each of those to know which one to ask — the interpretation the context's published interface
 * exists to own.
 *
 * WHICH MEANS TWO QUERIES, NOT ONE, and that is Firestore's shape rather than a choice. A document
 * missing a filtered field is EXCLUDED from the result, not returned last, so `clinic == X` cannot
 * see the catalog and no single filter reaches both. The union is assembled here, once, so nothing
 * above this has to.
 *
 * BOTH QUERIES ARE EQUALITY-ONLY, so Firestore serves them by merging single-field indexes and no
 * composite index is needed. That is a consequence of resolving validity in memory rather than in the
 * query — pushing `validFrom <= asOf` down would need an index that must ship before the code AND
 * would exclude any document missing the field, which is the mechanism behind more than one outage in
 * this pattern. If a future reader adds a range filter, its index ships first.
 *
 * THE WHOLE SET IS READ IN ONE GO, and the sizing was re-checked when org codes were added. The
 * catalog is curated and in the low tens; a clinic's own list is expected in the same range, since
 * an org authors the codes one or two funders require rather than a code system. Two reads per
 * composable instance, both small. The trigger for revisiting is a clinic past roughly a thousand
 * codes, or the arrival of a range filter — not a guess about growth.
 *
 * TOMBSTONES ARE NOT FILTERED. `isDeleted` is carried to the model and excluded by `isBillableOn`, so
 * a tombstoned code cannot be billed — but a caller resolving a historical reference still gets the
 * record rather than a dangling id. Filtering at the query would make a past service's code
 * unresolvable, which is the failure soft-delete exists to prevent.
 *
 * WRITES TOUCH `custom` DOCUMENTS ONLY. There is no path here that could create or change a managed
 * code, and the rules refuse one anyway: the catalog is seeded and maintained through the admin
 * SDK. A managed code stays unwritable from a browser whatever else changes.
 */

/**
 * The clinic scope, or a thrown error.
 *
 * REFUSED RATHER THAN NARROWED, and the narrowing is the tempting mistake. Without a clinic the
 * managed half of the read still succeeds, so the caller gets a well-formed catalog that is
 * missing exactly the org's own codes — indistinguishable from a clinic that has authored none. The
 * screen then invites somebody to mint a code they already own, which is the shadow-create the whole
 * governance design exists to prevent. Same reading `chargeMasterApi` gives an unreadable clinic.
 *
 * @param {*} clinicId
 * @returns {string}
 */
const requireClinicId = (clinicId) => {
  if (typeof clinicId !== 'string' || clinicId.length === 0) {
    throw new TypeError(
      `A service code read needs a clinic; got ${JSON.stringify(clinicId)}.`,
    );
  }
  return clinicId;
};

/**
 * Tombstone fields — the platform's soft-delete trio, matching `coverageApi`, `fundingSourceApi` and
 * `chargeMasterApi` exactly rather than coining a synonym: a retention sweep is cross-collection by
 * nature and one written against the established names would skip a tombstone called anything else.
 *
 * `deleteTimestamp` is a Firestore `Timestamp` and not a 'YYYY-MM-DD' string. `validFrom` and
 * `retiredOn` are calendar dates and must never be instants; a deletion is a moment, and the clock is
 * the server's so a wrong device clock cannot move a retention window.
 */
const tombstone = () => ({
  isDeleted: true,
  deleteTimestamp: serverTimestamp(),
  // `auth` ITSELF is optional-chained, not just `currentUser`. It is a mutable export torn down and
  // re-initialized on a region switch, so it is null for a window — and a tombstone written in that
  // window would throw a TypeError instead of recording who removed what.
  deletedBy: auth?.currentUser?.uid ?? null,
});

/**
 * Every code the clinic can reference: the whole managed catalog plus its own.
 *
 * UNRESOLVED AND UNSORTED, deliberately. Which codes may be billed depends on the date the caller
 * cares about, and ordering is a presentation decision; imposing either here would make every caller
 * that wanted something else fight it. Retired and tombstoned codes are included for the same reason
 * — a stored reference must resolve whatever state its code is in.
 *
 * THE TWO READS RUN CONCURRENTLY and are concatenated in a fixed order, managed first. Nothing
 * downstream depends on that order — the by-string lookup ranks by `origin` explicitly rather than by
 * position, precisely so it cannot come to depend on it — but a stable order keeps a rendered list
 * from reshuffling between loads.
 *
 * @param {{ clinicId: string }} scope
 * @returns {Promise<import('../../domain/model/ServiceCode.types').ServiceCode[]>}
 */
export async function loadServiceCodes({ clinicId }) {
  const clinic = requireClinicId(clinicId);
  const [managed, own] = await Promise.all([
    // Every managed document carries `origin`, written by the seed — which is what makes this filter
    // safe. Were one missing it, the document would vanish from the catalog entirely rather than
    // arriving unclassified, and a code nobody can find is a code somebody re-creates.
    new ServiceCodesDB().readAll([['origin', '==', SERVICE_CODE_ORIGIN.MANAGED]]),
    new ServiceCodesDB().readAll([['clinic', '==', clinic]]),
  ]);

  return toServiceCodes(managed.concat(own));
} // loadServiceCodes()

/**
 * Creates one org-authored code, or revives one the clinic removed by mistake.
 *
 * THE ID IS DERIVED FROM THE CLINIC, THE CODE STRING AND THE START DATE, which together with the
 * create-only rule is the whole guard against a clinic holding the same code for the same period
 * twice. No rule can count documents and no query is raceable — two writers would each query, each
 * see nothing, and each write — so uniqueness has to be unrepresentable rather than checked. Both
 * writers compute `{clinic}_{code}_{validFrom}`, aim at one document, and the rules refuse the
 * second. A DIFFERENT start date is a different document on purpose: the same code can be revised
 * for a new period, exactly as one in the catalog can.
 *
 * IT READS BEFORE WRITING so the refusal is legible. A losing writer's create is denied as
 * `permission-denied`, which reads identically to "you are not a manager at this clinic"; looking
 * first turns the recoverable case into a `ServiceCodeAlreadyExists` the dialog can explain. The read
 * is a race, not a lock — the rules are the real guard and this only decides what the user is told.
 *
 * A TOMBSTONED DOCUMENT AT THAT ID IS OVERWRITTEN, which is what makes a mistyped code correctable
 * at all. Intrinsic facts are immutable, so the only way to fix a wrong unit basis is to discard the
 * code and enter it again — and re-entering the same code for the same start date aims straight back
 * at the tombstone. Overwriting it is not rewriting history: a tombstone says the record should never
 * have existed, so there is nothing true to preserve. Reachable exactly as you would expect — mint
 * `200`, discard the row because the basis was wrong, mint it again.
 *
 * It is an UPDATE rather than a create, because the document is still there. The rules allow a full
 * rewrite only in this one direction (tombstoned → live); every other update is limited to the
 * lifespan fields.
 *
 * NOT WRAPPED IN A TRANSACTION, unlike its charge-master sibling, and the difference is real rather
 * than an inconsistency. That one appends to a series whose next version number is computed from
 * what it read, so the read is part of the decision. Here the id depends on nothing but the user's
 * own input, so a transaction would protect a computation that cannot go stale.
 *
 * @param {import('../../application/ports/ServiceCodeRepository.port').MintServiceCodeRequest} request
 * @returns {Promise<string>} the code's id
 * @throws {ServiceCodeAlreadyExists} the clinic already holds that code, whatever its dates
 */
export async function createServiceCode(request) {
  const clinic = requireClinicId(request?.clinicId);
  const id = customCodeDocumentId(clinic, request.code, request.validFrom);
  const serviceCodesDb = new ServiceCodesDB();
  // BOTH EMPTY VALUES, because the two backends disagree about what "missing" is: the Firestore path
  // returns null and the IndexedDB path hands back `undefined` straight from the store.
  const occupied = await serviceCodesDb.read(id);
  const document = toDocument({ ...request, clinicId: clinic });

  if (occupied !== null && occupied !== undefined) {
    if (occupied.isDeleted !== true) {
      throw new ServiceCodeAlreadyExists(request.code, request.validFrom);
    }
    // Reviving a tombstone. The whole document is restated, so the code comes back with the facts
    // just entered rather than the ones that were wrong — which is the entire point of the path.
    //
    // THE TOMBSTONE STAMPS ARE CLEARED, not left behind. `toDocument` sets `isDeleted` false but
    // knows nothing of the trio's other two fields, and a live document still carrying a
    // `deleteTimestamp` is exactly what a cross-collection retention sweep looks for — it would
    // purge a code the clinic is billing on.
    await serviceCodesDb.update({
      id, ...document, deleteTimestamp: null, deletedBy: null,
    });

    return id;
  }
  // The id is the SECOND argument, not a field: `create` deletes `data.id` before writing, so a
  // document id passed inside the payload is silently dropped and Firestore mints an auto-id — which
  // would defeat the derived-id guard above without failing anything.
  await serviceCodesDb.create(document, id);

  return id;
} // createServiceCode()

/**
 * Does any treatment service point at this code?
 *
 * THE HALF OF "IS IT REFERENCED" THE CHARGE-MASTER EDITOR CANNOT SEE. It holds the clinic's sheets
 * and can answer for those; `treatmentService.defaultServiceCodeId` is the other reference this
 * codebase has, and nothing on that screen reads it. Answering only from the sheets made
 * `hasCommittedReference` claim more than it checked — safe today, only because the treatment-service
 * picker narrows to codes already on a saved sheet, which is a coupling in a different file rather
 * than a property of this check.
 *
 * READS `treatmentServices` FROM THIS CONTEXT, which is the ACL layer's job and not a boundary
 * crossing: that collection belongs to no context, and `src/fb` is how the app reaches it. The
 * context still never interprets a treatment service — it asks one yes/no question about a
 * reference to a code it owns.
 *
 * TWO EQUALITY FILTERS, so Firestore serves this by merging single-field indexes and no composite
 * index ships with it. Removal is a rare, deliberate action, so one read at the moment of the
 * decision beats a flag on the code that every writer would have to remember to set — and that could
 * only ever be wrong in the direction that says a used code is free.
 *
 * @param {{ clinicId: string, serviceCodeId: string }} scope
 * @returns {Promise<boolean>}
 */
export async function hasTreatmentServiceReference({ clinicId, serviceCodeId }) {
  const services = await new TreatmentServicesDB().readAll([
    ['clinic', '==', requireClinicId(clinicId)],
    ['defaultServiceCodeId', '==', serviceCodeId],
  ]);

  return services.length > 0;
} // hasTreatmentServiceReference()

/**
 * Sets or clears a code's retirement date.
 *
 * THE ONLY FIELD A CLIENT MAY MOVE ON AN EXISTING CODE, besides the tombstone. Everything else is
 * intrinsic and immutable — changing `unitBasis` or `validFrom` would change what every existing
 * reference means, with nothing on the record to show it happened. The rules enforce that; this
 * function's shape is where a reader sees it.
 *
 * A NULL DATE UN-RETIRES, and is written explicitly rather than deleted, so the document keeps saying
 * "no end known" rather than going quiet about it.
 *
 * @param {{ serviceCodeId: string, retiredOn: string|null }} request
 * @returns {Promise<void>}
 */
export async function retireServiceCode({ serviceCodeId, retiredOn }) {
  await new ServiceCodesDB().update({ id: serviceCodeId, retiredOn });
} // retireServiceCode()

/**
 * Disowns a code — the platform soft-delete trio, never a hard delete.
 *
 * THE RULES GRANT NO `delete` ON THIS COLLECTION AT ALL, so this is not a policy choice made here. A
 * removed code that a treatment service or a past sheet still references must stay resolvable, or
 * every one of those references becomes a permanent hold with no record of what the code meant.
 *
 * @param {{ serviceCodeId: string }} request
 * @returns {Promise<void>}
 */
export async function removeServiceCode({ serviceCodeId }) {
  await new ServiceCodesDB().update({ id: serviceCodeId, ...tombstone() });
} // removeServiceCode()
